import {createContext, useContext, useReducer, type ReactNode} from 'react';
import {rejoinStorage} from "./rejoinStorage.ts"
import type {
    PlayerInfo,
    CommonRoomData,
    PhaseView,
    WaitingRoomView,
    SelectArticleView,
    WordPickerView,
    ResultsView,
    VotingView,
    VotingResultsView,
    GameOverView,
    PromptEntryView,
    DigitalPromptEntryView,
    RoundLeaderboardView
} from "./protocol.ts"
import type { ConnectionStatus } from "./webSocketContext.ts"

export type GamePhase =
    | "enter_room_code"
    | "waiting_room"
    | "selecting_articles"
    | "prompt_entry"
    | "digital_prompt_entry"
    | "word_picker"
    | "results"
    | "voting"
    | "voting_results"
    | "round_leaderboard"
    | "game_over"

export interface GameBaseState {
    /** Rich connection lifecycle. UI components can render reconnecting /
     *  failed / kicked states off this without needing extra props. */
    connection: ConnectionStatus;
    error: string | null;
    roomId: string | null;
    players: PlayerInfo[];
    self: PlayerInfo | null;
}

export type GameState = GameBaseState & (
    | { phase: "enter_room_code"; roomId: null; self: null; players: []; common: null; view: null }
    | { phase: "waiting_room"; roomId: string; self: PlayerInfo; players: PlayerInfo[]; common: CommonRoomData; view: WaitingRoomView }
    | { phase: "selecting_articles"; roomId: string; self: PlayerInfo; players: PlayerInfo[]; common: CommonRoomData; view: SelectArticleView }
    | { phase: "prompt_entry"; roomId: string; self: PlayerInfo; players: PlayerInfo[]; common: CommonRoomData; view: PromptEntryView }
    | { phase: "digital_prompt_entry"; roomId: string; self: PlayerInfo; players: PlayerInfo[]; common: CommonRoomData; view: DigitalPromptEntryView }
    | { phase: "word_picker"; roomId: string; self: PlayerInfo; players: PlayerInfo[]; common: CommonRoomData; view: WordPickerView }
    | { phase: "results"; roomId: string; self: PlayerInfo; players: PlayerInfo[]; common: CommonRoomData; view: ResultsView }
    | { phase: "voting"; roomId: string; self: PlayerInfo; players: PlayerInfo[]; common: CommonRoomData; view: VotingView }
    | { phase: "voting_results"; roomId: string; self: PlayerInfo; players: PlayerInfo[]; common: CommonRoomData; view: VotingResultsView }
    | { phase: "round_leaderboard"; roomId: string; self: PlayerInfo; players: PlayerInfo[]; common: CommonRoomData; view: RoundLeaderboardView }
    | { phase: "game_over"; roomId: string; self: PlayerInfo; players: PlayerInfo[]; common: CommonRoomData; view: GameOverView }
    )

interface GameStateContextType {
    state: GameState;
    set: <K extends keyof GameBaseState>(field: K, value: GameBaseState[K]) => void;
    updateSnapshot: (phase: string, common: CommonRoomData, self: PlayerInfo, view: PhaseView) => void;
    resetGame: () => void;
}

type GameAction =
    | { [K in keyof GameBaseState]: { type: 'set'; field: K; value: GameBaseState[K] } }[keyof GameBaseState]
    | { type: 'update_snapshot'; phase: string; common: CommonRoomData; self: PlayerInfo; view: PhaseView }
    | { type: 'reset' };

const GameStateContext = createContext<GameStateContextType | undefined>(undefined);

interface GameStateProviderProps {
    children: ReactNode;
}

// eslint-disable-next-line react-refresh/only-export-components
export const initialState: GameState = {
    phase: "enter_room_code",
    roomId: null,
    self: null,
    players: [],
    connection: { kind: "idle" },
    error: null,
    common: null,
    view: null
};

// eslint-disable-next-line react-refresh/only-export-components
export function gameReducer(state: GameState, action: GameAction): GameState {
    switch (action.type) {
        case 'set':
            return {...state, [action.field]: action.value} as GameState;
        case 'update_snapshot': {
            const newPhase = action.phase.toLowerCase() as GamePhase;

            return {
                ...state,
                phase: newPhase,
                common: action.common,
                view: action.view,
                players: action.common.players,
                self: action.self,
                roomId: action.common.roomCode
            } as GameState;
        }
        case 'reset':
            // Preserve the current connection status across a reset — the WS
            // layer manages its own lifecycle and a state reset (game over,
            // kick, etc.) shouldn't reset the connection back to idle.
            return { ...initialState, connection: state.connection };
    }
}

export const GameStateProvider = ({children}: GameStateProviderProps) => {
    const [state, dispatch] = useReducer(gameReducer, initialState);

    const set = <K extends keyof GameBaseState>(field: K, value: GameBaseState[K]) =>
        dispatch({type: 'set', field, value} as GameAction);

    const updateSnapshot = (phase: string, common: CommonRoomData, self: PlayerInfo, view: PhaseView) =>
        dispatch({type: 'update_snapshot', phase, common, self, view});

    const resetGame = () => {
        rejoinStorage.clear();
        dispatch({type: 'reset'});
    };

    const value: GameStateContextType = {
        state,
        set,
        updateSnapshot,
        resetGame
    };

    return (
        <GameStateContext.Provider value={value}>
            {children}
        </GameStateContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useGameState = () => {
    const context = useContext(GameStateContext);
    if (context === undefined) {
        throw new Error('useGameState must be used within a GameStateProvider');
    }
    return context;
};
